import React, { useEffect, useRef } from 'react'
import { Chart, ChartConfiguration, registerables } from 'chart.js'

Chart.register(...registerables)

interface PriceHistoryChartProps {
  data: any
  platform: string
}

const PriceHistoryChart: React.FC<PriceHistoryChartProps> = ({ data, platform }) => {
  const chartRef = useRef<HTMLCanvasElement>(null)
  const chartInstance = useRef<Chart | null>(null)

  useEffect(() => {
    if (!chartRef.current || !data) return

    // Destroy existing chart
    if (chartInstance.current) {
      chartInstance.current.destroy()
    }

    const ctx = chartRef.current.getContext('2d')
    if (!ctx) return

    // Process data based on platform
    let chartData: any = {
      labels: [],
      datasets: []
    }

    // Handle array of price points or nested history
    const priceData = Array.isArray(data) ? data : data.history || data.priceHistory || []

    if (priceData.length > 0) {
      // Detect format based on first item
      const firstItem = priceData[0]
      
      if ('t' in firstItem && 'p' in firstItem) {
        // Polymarket CLOB format: { t: timestamp, p: price }
        const timestamps = priceData.map((h: any) => {
          const date = new Date(h.t * 1000)
          return date.toLocaleTimeString()
        })
        const yesData = priceData.map((h: any) => h.p)
        const noData = priceData.map((h: any) => 1 - h.p)

        chartData = {
          labels: timestamps,
          datasets: [
            {
              label: 'YES',
              data: yesData,
              borderColor: 'var(--sacred-success)',
              backgroundColor: 'transparent',
              tension: 0.1,
              pointRadius: 0,
              borderWidth: 1.5
            },
            {
              label: 'NO',
              data: noData,
              borderColor: 'var(--sacred-error)',
              backgroundColor: 'transparent',
              tension: 0.1,
              pointRadius: 0,
              borderWidth: 1.5
            }
          ]
        }
      } else if ('ts' in firstItem || 'timestamp' in firstItem) {
        // Generic format with yes/no prices
        const timestamps = priceData.map((h: any) => {
          const date = new Date(h.ts || h.timestamp)
          return date.toLocaleTimeString()
        })
        const yesData = priceData.map((h: any) => h.yes_price || h.yesPrice || 0.5)
        const noData = priceData.map((h: any) => h.no_price || h.noPrice || 0.5)

        chartData = {
          labels: timestamps,
          datasets: [
            {
              label: 'YES',
              data: yesData,
              borderColor: 'var(--sacred-success)',
              backgroundColor: 'transparent',
              tension: 0.1,
              pointRadius: 0,
              borderWidth: 1.5
            },
            {
              label: 'NO',
              data: noData,
              borderColor: 'var(--sacred-error)',
              backgroundColor: 'transparent',
              tension: 0.1,
              pointRadius: 0,
              borderWidth: 1.5
            }
          ]
        }
      }
    }

    const config: ChartConfiguration = {
      type: 'line',
      data: chartData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false,
        },
        plugins: {
          legend: {
            display: true,
            position: 'top',
            labels: {
              boxWidth: 12,
              padding: 10,
              font: {
                size: 10,
                family: 'SF Mono, monospace'
              },
              color: 'var(--sacred-text-dim)'
            }
          },
          tooltip: {
            backgroundColor: 'var(--sacred-bg)',
            titleColor: 'var(--sacred-text)',
            bodyColor: 'var(--sacred-text-dim)',
            borderColor: 'var(--sacred-border)',
            borderWidth: 1,
            padding: 8,
            titleFont: {
              size: 11,
              family: 'SF Mono, monospace'
            },
            bodyFont: {
              size: 10,
              family: 'SF Mono, monospace'
            },
            callbacks: {
              label: (context: any) => {
                return `${context.dataset.label}: ${(context.parsed.y * 100).toFixed(1)}%`
              }
            }
          }
        },
        scales: {
          x: {
            display: true,
            grid: {
              display: false,
              drawBorder: false
            },
            ticks: {
              display: false
            }
          },
          y: {
            display: true,
            min: 0,
            max: 1,
            grid: {
              color: 'var(--sacred-border)',
              drawBorder: false
            },
            border: {
              display: false
            },
            ticks: {
              color: 'var(--sacred-text-dim)',
              font: {
                size: 10,
                family: 'SF Mono, monospace'
              },
              callback: (value: any) => `${(value * 100).toFixed(0)}%`
            }
          }
        }
      }
    }

    chartInstance.current = new Chart(ctx, config)

    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy()
      }
    }
  }, [data, platform])

  return (
    <div style={{ height: '200px', position: 'relative' }}>
      <canvas ref={chartRef} />
    </div>
  )
}

export default PriceHistoryChart